import { TEXT_PAD, lineWidth, textFontString, wrapTextLines } from './drawObjects';

/**
 * Where every character of a text box sits on the page, as the canvas paints
 * it — so a selection drawn over canvas text, or a click turned into a
 * character offset, lines up with what the user sees.
 *
 * Mirrors `drawText` exactly: the same wrap (`wrapTextLines`), the same
 * TEXT_PAD inset, line pitch, alignment and justification slack. Offsets are
 * into `obj.text`. Characters the wrap drops (the space a line broke on, the
 * newline between paragraphs) have no position; a hyphen the wrap inserted has
 * no offset of its own.
 *
 * Positions are per grapheme, never inside one: a Bangla cluster is selected
 * and deleted whole, as every text editor does.
 */

let measure = null;
const ctxFor = (style) => {
    if (!measure) measure = document.createElement('canvas').getContext('2d');
    measure.font = textFontString(style);
    return measure;
};

const graphemes = (() => {
    const seg = typeof Intl !== 'undefined' && Intl.Segmenter
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : null;
    return (text) => (seg ? Array.from(seg.segment(text), (s) => s.segment) : Array.from(text));
})();

const lineHeightOf = (obj, line) => (line.fontSize || obj.fontSize) * (obj.lineHeight || 1.35);

/**
 * @returns {{ lines: Array<{ top: number, height: number, start: number, end: number,
 *   clusters: Array<{ start: number, end: number, x0: number, x1: number }> }> }}
 */
export function layoutText(obj) {
    const text = String(obj.text ?? '');
    const lines = wrapTextLines(obj);
    const inner = obj.w - TEXT_PAD * 2;
    const left = obj.x + TEXT_PAD;
    let top = obj.y + TEXT_PAD;
    let p = 0;
    const out = [];

    lines.forEach((line, index) => {
        const height = lineHeightOf(obj, line);
        // A new paragraph begins after the newline the previous one ended on;
        // a wrapped line begins after the space the wrap swallowed.
        if (index > 0) {
            if (lines[index - 1].last) {
                while (p < text.length && text[p] !== '\n' && /\s/.test(text[p])) p += 1;
                if (text[p] === '\n') p += 1;
            }
        }

        const measured = lineWidth(line);
        const gaps = obj.align === 'justify' && !line.last
            ? line.segments.filter((seg) => seg.space).length
            : 0;
        const slack = gaps && measured < inner ? (inner - measured) / gaps : 0;
        let pen = left;
        if (!slack) {
            if (obj.align === 'center') pen += (inner - measured) / 2;
            else if (obj.align === 'right') pen += inner - measured;
        }

        const clusters = [];
        const lineStart = p;
        for (const seg of line.segments) {
            const ctx = ctxFor(seg.style);
            let prefix = '';
            for (const g of graphemes(seg.text)) {
                const x0 = pen + ctx.measureText(prefix).width;
                prefix += g;
                const x1 = pen + ctx.measureText(prefix).width;
                // Line the glyph up with the source text, stepping over what
                // the wrap dropped (a space it broke on) — but only whitespace.
                while (p < text.length && !text.startsWith(g, p) && /\s/.test(text[p]) && text[p] !== '\n') p += 1;
                if (text.startsWith(g, p)) {
                    clusters.push({ start: p, end: p + g.length, x0, x1 });
                    p += g.length;
                }
                // Otherwise a hyphen the wrap inserted: painted, but not text.
            }
            pen += ctx.measureText(seg.text).width + (seg.space ? slack : 0);
        }
        const start = clusters.length ? clusters[0].start : lineStart;
        const end = clusters.length ? clusters[clusters.length - 1].end : lineStart;
        out.push({ top, height, start, end, clusters, left, right: left + inner });
        top += height;
    });

    return { lines: out };
}

/** The character offset a page-space point lands on: before the nearer half of a cluster. */
export function offsetAt(obj, point, layout = layoutText(obj)) {
    const { lines } = layout;
    if (!lines.length) return 0;
    let line = lines[lines.length - 1];
    if (point.y < lines[0].top) line = lines[0];
    else {
        for (const l of lines) {
            if (point.y < l.top + l.height) { line = l; break; }
        }
    }
    if (!line.clusters.length) return line.start;
    for (const c of line.clusters) {
        if (point.x < (c.x0 + c.x1) / 2) return c.start;
    }
    // Past the last character: the end of the line — and of a paragraph-closing
    // line, that is also where its newline begins.
    return line.end;
}

/** Page-space rectangles covering `[start, end)` — one per line it touches. */
export function rangeRects(obj, start, end, layout = layoutText(obj)) {
    const rects = [];
    if (end <= start) return rects;
    for (const line of layout.lines) {
        const inRange = line.clusters.filter((c) => c.start >= start && c.end <= end);
        if (inRange.length) {
            const x0 = Math.min(...inRange.map((c) => c.x0));
            let x1 = Math.max(...inRange.map((c) => c.x1));
            // A selection that runs on past the end of this line shows a sliver
            // past its last character, as Word marks the line break.
            if (end > line.end && inRange[inRange.length - 1] === line.clusters[line.clusters.length - 1]) {
                x1 += Math.max(2, (obj.fontSize || 12) * 0.3);
            }
            rects.push({ x: x0, y: line.top, w: Math.max(1, x1 - x0), h: line.height });
        } else if (!line.clusters.length && line.start >= start && line.start < end) {
            // An empty line inside the selection.
            rects.push({ x: line.left, y: line.top, w: Math.max(2, (obj.fontSize || 12) * 0.3), h: line.height });
        }
    }
    return rects;
}
